import type {
  ActionV3,
  EpisodeV3,
  ExtensionMap,
  JsonValue,
  SceneV3,
  SpeakerV3,
  StateV3,
} from "../../script/v3/types.js";
import type {
  AuthoringDeclaration,
  AuthoringDocument,
  AuthoringTarget,
} from "./types.js";

function escapeHeader(value: string): string {
  return value
    .replace(/\\/g, "\\\\")
    .replace(/\|/g, "\\|")
    .replace(/\]/g, "\\]")
    .replace(/\n/g, "\\n");
}

function escapeContent(value: string): string {
  return value
    .replace(/\\/g, "\\\\")
    .replace(/\n/g, "\\n");
}

function record(type: string, slots: string[], content: string): string {
  const header = [type, ...slots].map(escapeHeader).join("|");
  const body = escapeContent(content);
  return `[${header}]${body.length > 0 ? ` ${body}` : ""}`;
}

function canonicalizeJson(value: JsonValue): JsonValue {
  if (Array.isArray(value)) return value.map(canonicalizeJson);
  if (value !== null && typeof value === "object") {
    const result: Record<string, JsonValue> = {};
    for (const key of Object.keys(value).sort()) result[key] = canonicalizeJson(value[key]!);
    return result;
  }
  return value;
}

function extensionLines(extend: ExtensionMap | undefined): string[] {
  if (extend === undefined) return [];
  return [
    record("extensions", [], ""),
    ...Object.keys(extend)
      .sort()
      .map((key) => record("extend", [key], JSON.stringify(canonicalizeJson(extend[key]!)))),
  ];
}

function speakerSlot(speaker: SpeakerV3): string {
  if ("actor_id" in speaker) {
    return speaker.state_id === undefined ? speaker.actor_id : `${speaker.actor_id}/${speaker.state_id}`;
  }
  return speaker.label === undefined ? speaker.kind : `${speaker.kind}:${speaker.label}`;
}

function actionLines(action: ActionV3): string[] {
  const lines = extensionLines(action.extend);
  if (action.transition_prompt !== undefined) {
    lines.push(record("prompt", [
      action.transition_prompt.target,
      action.transition_prompt.process,
      action.transition_prompt.contrast,
    ], ""));
  }
  if (action.type === "dialogue" || action.type === "inner_thought") {
    const type = action.type === "dialogue" ? "dlg" : "think";
    const slots = [speakerSlot(action.speaker!)];
    if (action.emotion !== undefined) slots.push(action.emotion);
    lines.push(record(type, slots, action.content));
    return lines;
  }
  const type = action.type === "action" ? "act" : "trans";
  lines.push(record(type, action.emotion === undefined ? [] : [action.emotion], action.content));
  return lines;
}

function sceneLines(scene: SceneV3, attrs: string): string[] {
  const lines = [...extensionLines(scene.extend), `<scene ${attrs}>`];
  if (scene.environment !== undefined) {
    if (scene.environment.time !== undefined) {
      lines.push(record("environment", [scene.environment.space ?? "", scene.environment.time], ""));
    } else if (scene.environment.space !== undefined) {
      lines.push(record("environment", [scene.environment.space], ""));
    } else {
      lines.push(record("environment", [], ""));
    }
  }
  if (scene.location === null) {
    lines.push(record("location", [], ""));
  } else {
    const slots = [scene.location.location_id];
    if (scene.location.state_id !== undefined) slots.push(scene.location.state_id);
    lines.push(record("location", slots, ""));
  }
  for (const actor of scene.actors) {
    const slots = [actor.actor_id];
    if (actor.state_id !== undefined) slots.push(actor.state_id);
    lines.push(record("cast", slots, ""));
  }
  for (const prop of scene.props) {
    const slots = [prop.prop_id];
    if (prop.state_id !== undefined) slots.push(prop.state_id);
    lines.push(record("prop", slots, ""));
  }
  lines.push("", "<body>");
  for (const action of scene.actions) lines.push(...actionLines(action));
  lines.push("</body>", "", "</scene>");
  return lines;
}

function episodeLines(episode: EpisodeV3): string[] {
  const lines = [...extensionLines(episode.extend), `<episode id=${episode.episode_id}>`];
  if (episode.title !== undefined) lines.push(record("title", [], episode.title));
  if (episode.synopsis !== undefined) lines.push(record("synopsis", [], episode.synopsis));
  if ((episode.title !== undefined || episode.synopsis !== undefined) && episode.scenes.length > 0) lines.push("");
  episode.scenes.forEach((scene, index) => {
    lines.push(...sceneLines(scene, `id=${scene.scene_id}`));
    if (index < episode.scenes.length - 1) lines.push("");
  });
  lines.push("</episode>");
  return lines;
}

function assetDeclarationLines(declaration: AuthoringDeclaration): string[] {
  if (declaration.declarationKind === "state") return stateDeclarationLines(declaration.ownerKind, declaration.ownerId, declaration.value);
  const asset = declaration.value;
  const lines = extensionLines(asset.extend);
  if (asset.aliases !== undefined) lines.push(record("aliases", [], ""));
  for (const alias of asset.aliases ?? []) lines.push(record("alias", [], alias));
  if (declaration.declarationKind === "actor") {
    const actor = declaration.value;
    const slots = [actor.actor_id, actor.actor_name, actor.importance];
    if (actor.role_type !== undefined) slots.push(actor.role_type);
    lines.push(record("actor", slots, actor.description));
  } else if (declaration.declarationKind === "location") {
    const location = declaration.value;
    lines.push(record("location", [location.location_id, location.location_name, location.importance], location.description));
  } else {
    const prop = declaration.value;
    lines.push(record("prop", [prop.prop_id, prop.prop_name, prop.importance], prop.description));
  }
  return lines;
}

function stateDeclarationLines(kind: string, ownerId: string, state: StateV3): string[] {
  return [
    ...extensionLines(state.extend),
    record("state", [`${kind}:${ownerId}`, state.state_id, state.state_name], state.description),
  ];
}

function targetLines(target: AuthoringTarget): string[] {
  switch (target.kind) {
    case "script": {
      const lines = [...extensionLines(target.extend), "<script>"];
      lines.push(record("title", [], target.title));
      lines.push(record("worldview", [], target.worldview));
      if (target.worldviewRaw !== undefined) lines.push(record("worldview-raw", [], target.worldviewRaw));
      lines.push(record("style", [], target.style));
      if (target.synopsis !== undefined) lines.push(record("synopsis", [], target.synopsis));
      if (target.logline !== undefined) lines.push(record("logline", [], target.logline));
      if (target.theme !== undefined) lines.push(record("theme", [], target.theme));
      if (target.mainCharacters !== undefined) {
        lines.push(record("main-characters", [], ""));
        for (const character of target.mainCharacters) {
          lines.push(record("main-character", [character.name, character.role], ""));
        }
      }
      if (target.episodes.length > 0) lines.push("");
      target.episodes.forEach((episode, index) => {
        lines.push(...episodeLines(episode));
        if (index < target.episodes.length - 1) lines.push("");
      });
      lines.push("</script>");
      return lines;
    }
    case "episode":
      return episodeLines(target.value);
    case "episode-span": {
      const lines = [
        ...extensionLines(target.extend),
        `<episode-span from=${target.from} through=${target.through}>`,
      ];
      target.episodes.forEach((episode, index) => {
        lines.push(...episodeLines(episode));
        if (index < target.episodes.length - 1) lines.push("");
      });
      lines.push("</episode-span>");
      return lines;
    }
    case "scene":
      return sceneLines(target.value, `id=${target.value.scene_id} parent=${target.parent}`);
    case "scene-span": {
      const lines = [
        ...extensionLines(target.extend),
        `<scene-span parent=${target.parent} from=${target.from} through=${target.through}>`,
      ];
      target.scenes.forEach((scene, index) => {
        lines.push(...sceneLines(scene, `id=${scene.scene_id}`));
        if (index < target.scenes.length - 1) lines.push("");
      });
      lines.push("</scene-span>");
      return lines;
    }
    case "action-span": {
      const lines = [
        ...extensionLines(target.extend),
        `<action-span parent=${target.parent} from=${target.from} through=${target.through}>`,
        "<body>",
      ];
      for (const action of target.actions) lines.push(...actionLines(action));
      lines.push("</body>", "</action-span>");
      return lines;
    }
  }
}

export function serializeAuthoring(document: AuthoringDocument): string {
  const lines = ["<authoring version=1>", ""];
  if (document.declarations.length > 0) {
    lines.push("<create>");
    for (const declaration of document.declarations) lines.push(...assetDeclarationLines(declaration));
    lines.push("</create>", "");
  }
  document.targets.forEach((target, index) => {
    lines.push(...targetLines(target));
    if (index < document.targets.length - 1) lines.push("");
  });
  lines.push("", "</authoring>");
  return `${lines.join("\n")}\n`;
}
